// Builds the "Menu Engineering" master prompt from a saved perfil.
// The prompt embeds the real business name, products, prices, margins and the
// current day of the week so the LLM can classify Estrella / Caballo / Puzzle / Perro
// and give same-day executable actions.

import { BUSINESS_LABEL } from "../data/businesses";

const RED_LABEL = {
  facebook: "Facebook",
  instagram: "Instagram",
  whatsapp: "WhatsApp",
  tiktok: "TikTok",
};

// Best publishing hours per giro + weekday. weekday is JS getDay() output:
// 0=domingo, 1=lunes, ..., 6=sábado. Returns an array of 1-2 hour strings.
function bestHoursForToday(giro, weekday) {
  const isWeekend = weekday === 0 || weekday === 6;
  switch (giro) {
    case "taqueria":
      return isWeekend ? ["1pm", "8pm"] : ["12pm", "7pm"];
    case "polleria":
      return ["11am", "2pm"];
    case "bar":
      return ["7pm", "10pm"];
    case "gym":
      if (weekday === 1) return ["7am", "6pm"];
      return isWeekend ? ["9am", "11am"] : ["7am", "6pm"];
    case "ropa":
      if (weekday === 4) return ["8pm"];      // jueves
      if (weekday === 6) return ["11am"];     // sábado
      return ["11am", "8pm"];
    case "puesto":
      return ["12pm", "2pm"];
    case "abarrotes":
      return ["8am", "6pm"];
    case "venta_redes":
      return ["12pm", "9pm"];
    case "cafeteria":
      return ["8am", "4pm"];
    case "estetica":
      return ["11am", "5pm"];
    case "tortilleria":
      return ["7am", "12pm"];
    default:
      return ["12pm", "7pm"];
  }
}

function fmtMoney(n) {
  // Show integers as "$120", decimals as "$18.50". Never localise thousands
  // so the LLM parses them predictably.
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

function todayInSpanish() {
  try {
    const d = new Date().toLocaleDateString("es-MX", { weekday: "long" });
    return d.toUpperCase();
  } catch {
    // Older Safari/Node fallback
    const dias = ["DOMINGO", "LUNES", "MARTES", "MIÉRCOLES", "JUEVES", "VIERNES", "SÁBADO"];
    return dias[new Date().getDay()];
  }
}

function weekdayIndex() {
  try {
    return new Date().getDay();
  } catch {
    return 1;
  }
}

export function buildMenuEngineeringPrompt(perfil) {
  const giroLabel = BUSINESS_LABEL[perfil.giro] || "negocio";
  const red = RED_LABEL[perfil.redPrincipal] || "redes sociales";
  const dia = todayInSpanish();
  const horas = bestHoursForToday(perfil.giro, weekdayIndex());
  const horasStr = horas.join(" y ");

  const productoLines = perfil.productos.map((p) => {
    const nombre = p.nombre.trim();
    const precio = Number(p.precio) || 0;
    const costo = p.costo != null && p.costo !== "" ? Number(p.costo) : null;
    if (costo != null && !Number.isNaN(costo) && costo >= 0 && precio > 0) {
      const margenPesos = precio - costo;
      const margenPct = ((precio - costo) / precio) * 100;
      return `- ${nombre}: precio $${fmtMoney(precio)}, costo $${fmtMoney(
        costo,
      )}, margen $${fmtMoney(margenPesos)} (${Math.round(margenPct)}%)`;
    }
    return `- ${nombre}: precio $${fmtMoney(precio)}`;
  });

  return `Actúa como consultor de marketing experto en Menu Engineering (Kasavana & Smith, Cornell University 1982) y estrategias de ventas para pequeños negocios mexicanos.

MI NEGOCIO: ${perfil.nombreNegocio.trim()} · ${giroLabel} · ${perfil.ciudad.trim()}
MI RED PRINCIPAL: ${red}

MIS PRODUCTOS:
${productoLines.join("\n")}

HOY ES ${dia}.

MOMENTO IDEAL PARA PUBLICAR HOY: ${horasStr}. Ajusta el horario de la publicación a estas horas.

Con base en Menu Engineering, haz lo siguiente:

1. Clasifica cada producto como Estrella / Caballo de batalla / Puzzle / Perro según popularidad esperada y margen real.
2. Dime exactamente cuál promover HOY y por qué.
3. Dame el texto completo listo para publicar en ${red} con el nombre real del producto. Sin corchetes ni ejemplos — el texto final que el dueño copia y pega directo.
4. Si hay un producto popular de bajo margen, sugiere un combo concreto con precio específico para subir la ganancia.
5. Dame UNA acción ejecutable en los próximos 30 minutos solo con el celular, sin presupuesto adicional.

Responde en español mexicano informal. Directo y sin tecnicismos. Cada recomendación debe ser accionable hoy.`;
}
